'use client';

import '@/styles/news-styles.css';

import { faFacebook, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpFromBracket, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExtraSmallNewsCard from '@pickleballinc/features/src/hub-cards/ExtraSmallNewsCard/ExtraSmallNewsCard';
import { Modal, ModalContent } from '@pickleballinc/react-ui';
import getCDNURL from '@pickleballinc/ui/components/pickleball-app/utils/getCDNURL';
import parse from 'html-react-parser';
import moment from 'moment-timezone';
import Script from 'next/script';
import { Fragment, useEffect, useRef, useState } from 'react';

import EditButton from '@/blog/article_components/EditButton';
import FavoriteButton from '@/blog/article_components/FavoriteButton';
import ImagePreviewAndZoom from '@/blog/article_components/ImagePreviewAndZoom';
import ShareDropdown from '@/blog/article_components/ShareDropdown';
import { RenderBreadcrumbs } from '@/blog/components/RenderBreadcrumbs';
import { BLOG_SLUG } from '@/blog/constants';
import { useSession } from '@/lib/SessionProvider';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { RelatedNewsInterface } from '@/types/blog.types';
import { Favorites } from '@/utils/actions/blog/areTheyFavorite';
import { MainContentNewsInterface } from '@/utils/actions/blog/getNewsBySlug';

declare global {
	interface Window {
		instgrm?: {
			Embeds: {
				process: () => void;
			};
		};
	}
}

export interface ImgInfo {
	src: string;
	alt: string;
}

export interface MainContentProps {
	news: MainContentNewsInterface;
	relatedNews: RelatedNewsInterface[];
	favorite?: Favorites;
	totalFavoritesCount: string;
}

const MainContent = ({ news, relatedNews, favorite, totalFavoritesCount }: MainContentProps) => {
	const {
		title,
		authorFullName,
		content,
		facebookLink,
		imageAltText,
		imageCaption,
		imageSource,
		imageUrl,
		publishDateDisplayed,
		smsLink,
		twitterLink,
		newsCategory,
		slug,
		uuid
	} = news;
	const newsCategorySlug = newsCategory?.slug || BLOG_SLUG;

	const articleURL = `${process.env.NEXT_PUBLIC_REWRITE_DOMAIN_NAME?.includes('localhost') ? 'http' : 'https'}://${
		process.env.NEXT_PUBLIC_REWRITE_DOMAIN_NAME
	}/${newsCategorySlug}/${slug}`;

	const { session } = useSession();
	const loggedIn = session?.user;
	const { hasPermission } = useAuth();
	const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

	const shareDropdownRef = useRef<HTMLUListElement>(null);

	const [shareDropdownOpened, setShareDropdownOpened] = useState<boolean>(false);
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [isFavorited, setIsFavorited] = useState(favorite?.module_uuid !== undefined);
	const [favoritesCount, setFavoritesCount] = useState(totalFavoritesCount ? parseInt(totalFavoritesCount) : 0);

	const [imgInfo, setImgInfo] = useState<ImgInfo>({ src: '', alt: '' });

	const handleClick = (event: any) => {
		event.stopPropagation();
		const imgSrc = event.target.getAttribute('src');
		const imgAlt = event.target.getAttribute('alt');
		setImgInfo({ src: imgSrc, alt: imgAlt });
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
				setShareDropdownOpened(false);
				setIsCopied(false);
			}
		};

		document.body.addEventListener('click', handleClickOutside);

		return () => {
			document.body.removeEventListener('click', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const imageContainer = document.getElementById('news-content');

		if (imageContainer) {
			const images = imageContainer.querySelectorAll('img');

			images.forEach((imgElement) => {
				if (imgElement) {
					imgElement.addEventListener('click', handleClick);
				}
			});

			return () => {
				images.forEach((imgElement) => {
					if (imgElement) {
						imgElement.removeEventListener('click', handleClick);
					}
				});
			};
		}

		return () => {};
	}, []);

	useEffect(() => {
		if (window.instgrm) window.instgrm.Embeds.process();
	}, []);

	return (
		<Fragment>
			<div className="flex p-0 sm:flex-1">
				<RenderBreadcrumbs categoryTitle={newsCategory.title} categorySlug={newsCategorySlug} slug={slug} newsTitle={title} />
			</div>
			{imgInfo.src && (
				<Modal open={imgInfo.src ? true : false} onOpenChange={() => setImgInfo({ src: '', alt: '' })}>
					<ModalContent className="flex !h-full flex-col items-center justify-center !border-none !bg-transparent !p-4 !outline-none sm:!p-8">
						<ImagePreviewAndZoom imgInfo={imgInfo} resetImgURL={() => setImgInfo({ src: '', alt: '' })} />
					</ModalContent>
				</Modal>
			)}
			<div className="flex size-full flex-col" itemScope itemType="https://schema.org/NewsArticle">
				<meta itemProp="url" content={articleURL} />
				<div className="flex flex-1 flex-col justify-center overflow-auto p-4 pt-5" id="news-content">
					<div className="w-full max-w-2xl bg-white">
						<figure className="mb-4 w-full overflow-hidden rounded-lg sm:min-h-[251px]">
							<img
								src={
									imageUrl ||
									`${getCDNURL(`${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleball-app/assets/images/placeholder.png`, 269, 269)}`
								}
								alt={imageAltText || title}
								data-default-src={
									imageUrl ||
									`${getCDNURL(`${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleball-app/assets/images/placeholder.png`, 269, 269)}`
								}
								className="block size-full max-h-[269px] bg-cover object-cover sm:min-h-[269px]"
								itemProp="thumbnail"
							/>
							{((imageCaption && imageCaption.length > 0) || (imageSource && imageSource.length > 0)) && (
								<figcaption className="mt-2.5 block bg-white px-0.5 text-xs leading-4 text-gray-500">
									{imageCaption && <>{imageCaption}&nbsp;</>}
									{imageSource && <cite className="not-italic text-[#a5a6a7]">{imageSource}</cite>}
								</figcaption>
							)}
						</figure>
						<h2
							className="relative z-30 mb-6 text-3xl font-bold leading-[1.2] text-gray-900 outline-none sm:text-4xl"
							itemProp="headline"
						>
							{title}
						</h2>
						<div className="mb-6 flex items-center gap-2 border-b border-gray-200 pb-3">
							<div className="flex gap-1">
								{/* TODO: Add author image when it arrives in payload
                 <Avatar size="sm" /> 
                */}
								<div className="flex flex-col justify-center gap-1">
									<div
										className="relative text-xs font-medium leading-none text-gray-900"
										itemScope
										itemProp="author"
										itemType="https://schema.org/Person"
									>
										<span itemProp="name">{authorFullName}</span>
									</div>
									{publishDateDisplayed && (
										<div
											className="relative text-xs leading-none text-[#344054]"
											itemProp="datePublished"
											content={moment(publishDateDisplayed).format('YYYY–MM-DD HH:mm:ss')}
										>
											{moment.utc(publishDateDisplayed).tz('America/New_York').format('MMM DD, YYYY hh:mm A [ET]')}
										</div>
									)}
								</div>
							</div>
							<div className="flex items-center gap-3 sm:justify-center">
								{facebookLink && (
									<a
										href={facebookLink}
										rel="noopener noreferrer"
										target="_blank"
										aria-label="Link to Facebook page (opens in a new tab)"
									>
										<FontAwesomeIcon className="text-gray-900" icon={faFacebook} size="xl" />
									</a>
								)}
								{twitterLink && (
									<a href={twitterLink} rel="noopener noreferrer" target="_blank" aria-label="Link to X page (opens in a new tab)">
										<FontAwesomeIcon className="text-gray-900" icon={faXTwitter} size="xl" />
									</a>
								)}
								{smsLink && (
									<a href={smsLink} rel="noopener noreferrer" target="_blank" aria-label="Link to SMS page (opens in a new tab)">
										<FontAwesomeIcon className="text-gray-900" icon={faEnvelope} size="xl" />
									</a>
								)}
							</div>
						</div>
						<div
							className="like-and-share relative mb-12 flex justify-between flex-wrap items-center"
							data-behavior="content_reactions"
							data-contenttitle={title}
						>
							<div className="flex flex-wrap mt-1">
								<div className="flex flex-row mr-3 sm:mr-0 sm:-ml-3 sm:pr-0">
									<button
										className="flex cursor-pointer flex-row items-center gap-2 rounded-[100px] sm:px-3 sm:py-1.5 sm:hover:bg-gray-100"
										onClick={() => setShareDropdownOpened(true)}
										style={{
											WebkitUserSelect: 'none',
											MozUserSelect: 'none',
											msUserSelect: 'none',
											userSelect: 'none'
										}}
									>
										<FontAwesomeIcon icon={faArrowUpFromBracket} className="size-4 sm:size-5" />
										<span className="text-sm font-semibold not-italic text-gray-900 sm:text-base sm:leading-5">Share</span>
									</button>
								</div>
								{shareDropdownOpened && (
									<ShareDropdown
										shareDropdownRef={shareDropdownRef}
										isCopied={isCopied}
										setIsCopied={setIsCopied}
										articleURL={articleURL}
										title={title}
									/>
								)}
								{isSuperAdmin && <EditButton articleSlug={slug} />}
								{loggedIn && (
									<FavoriteButton
										moduleUuid={uuid}
										isFavorited={isFavorited}
										setIsFavorited={setIsFavorited}
										setFavoritesCount={setFavoritesCount}
										favoritesCount={favoritesCount}
									/>
								)}
							</div>
							{favoritesCount > 0 && (
								<div className="text-sm sm:text-base mt-1">
									<span className="mr-2">
										<FontAwesomeIcon icon={faHeart} className="text-error-500 text-base" />
									</span>
									{isFavorited
										? favoritesCount === 1
											? 'You'
											: `You and ${favoritesCount - 1} ${favoritesCount === 2 ? 'other' : 'others'}`
										: favoritesCount}
								</div>
							)}
						</div>
						<div className="news-preview relative" itemProp="text">
							{content && parse(content)}
						</div>
					</div>
				</div>
				{relatedNews.length > 0 && (
					<div className="mt-6 px-4 pb-4">
						<h2 className="mb-6 text-3xl font-bold leading-[1.2] text-gray-900 outline-none sm:text-4xl">Related articles</h2>
						<div className="grid w-full max-w-2xl grid-cols-1 gap-4 pb-2 sm:grid-cols-2">
							{relatedNews.map((article) => (
								<a
									key={`related-article-${article.uuid}`}
									className="w-full hover:opacity-80"
									href={
										article.externalUrl ||
										`${article.categorySlug === BLOG_SLUG ? process.env.NEXT_PUBLIC_PT_URL : `https://${process.env.NEXT_PUBLIC_FRONT_URL}`}/${article.categorySlug}/${article.slug}`
									}
									rel="noopener noreferrer"
									target={article.externalUrl ? '_blank' : '_self'}
								>
									<ExtraSmallNewsCard
										oneLineForDescription
										image={article.imageUrl || article.smallNewsCardImgUrl || ''}
										imageAltText={article.imageAltText}
										title={article.title}
										description={article.description || ''}
										publishDateDisplayed={article.publishDateDisplayed}
										author={article.authorFullName || ''}
									/>
								</a>
							))}
						</div>
					</div>
				)}
			</div>
			<Script async defer strategy="lazyOnload" type="text/javascript" src="https://www.instagram.com/embed.js" />
			<Script async defer strategy="lazyOnload" type="text/javascript" src="https://platform.twitter.com/widgets.js" charSet="utf-8" />
		</Fragment>
	);
};

export default MainContent;
